import { styled, keyframes } from "styled-components";

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(3deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.05); }
`;

export const HeroSectionStyled = styled.section`
  width: 100%;
  min-height: 450px;
  align-items: center;
  justify-content: center;
  display: flex;
  background: var(--bg-secondary);
  padding: 120px 40px 100px;
  position: relative;
  overflow: hidden;

  .heroBg {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    overflow: hidden;

    .gradientOrb {
      position: absolute;
      border-radius: 50%;
      filter: blur(80px);

      &.orb1 {
        width: 500px;
        height: 500px;
        background: radial-gradient(
          circle,
          rgba(217, 58, 53, 0.15) 0%,
          transparent 70%
        );
        top: -200px;
        right: -100px;
        animation: ${pulse} 8s ease-in-out infinite;
      }

      &.orb2 {
        width: 400px;
        height: 400px;
        background: radial-gradient(
          circle,
          rgba(43, 59, 124, 0.15) 0%,
          transparent 70%
        );
        bottom: -150px;
        left: -100px;
        animation: ${pulse} 10s ease-in-out infinite reverse;
      }
    }

    .gridPattern {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-image: linear-gradient(
          var(--text-primary) 1px,
          transparent 1px
        ),
        linear-gradient(90deg, var(--text-primary) 1px, transparent 1px);
      background-size: 60px 60px;
      opacity: 0.02;
    }
  }

  .floatingShape {
    position: absolute;
    border-radius: 20px;
    animation: ${float} 6s ease-in-out infinite;

    &.shape1 {
      width: 80px;
      height: 80px;
      background: linear-gradient(135deg, var(--accent) 0%, transparent 100%);
      top: 15%;
      left: 8%;
      opacity: 0.3;
      animation-delay: 0s;
    }

    &.shape2 {
      width: 60px;
      height: 60px;
      background: linear-gradient(135deg, var(--primary) 0%, transparent 100%);
      top: 25%;
      right: 12%;
      opacity: 0.25;
      animation-delay: 2s;
      border-radius: 50%;
    }

    &.shape3 {
      width: 100px;
      height: 100px;
      border: 3px solid var(--accent);
      background: transparent;
      bottom: 15%;
      right: 8%;
      opacity: 0.2;
      animation-delay: 4s;
      border-radius: 50%;
    }
  }

  .heroSectionContainer {
    position: relative;
    max-width: 1200px;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 1;

    .heroContent {
      text-align: center;
      max-width: 800px;

      .heroLabel {
        display: inline-flex;
        align-items: center;
        gap: 16px;
        margin-bottom: 28px;

        .line {
          width: 40px;
          height: 2px;
          background: var(--accent);
        }

        span:not(.line) {
          font-family: Poppins;
          font-size: 14px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 3px;
          color: var(--accent);
        }
      }
    }

    .heroTitle {
      color: var(--text-primary);
      font-family: Poppins;
      font-size: 64px;
      font-weight: 800;
      line-height: 1.15;
      width: 100%;
      text-align: center;
      margin: 0 0 24px;

      .word {
        will-change: transform, opacity;

        &.highlight {
          background: linear-gradient(
            135deg,
            var(--accent) 0%,
            var(--primary) 100%
          );
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      }
    }

    .heroDescription {
      font-family: Poppins;
      font-size: 18px;
      line-height: 1.7;
      color: var(--text-secondary);
      margin: 0 0 32px;
      will-change: transform, opacity;
    }

    .breadcrumb {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      margin-top: 8px;
      will-change: opacity;

      a {
        font-family: Poppins;
        font-size: 14px;
        color: var(--text-secondary);
        text-decoration: none;
        transition: color 0.3s ease;

        &:hover {
          color: var(--accent);
        }
      }

      .separator {
        color: var(--text-secondary);
        opacity: 0.5;
      }

      .current {
        font-family: Poppins;
        font-size: 14px;
        color: var(--accent);
        font-weight: 600;
      }
    }

    .heroImageWrapper {
      margin-top: 60px;
      position: relative;
      max-width: 600px;
      width: 100%;

      .imageGlow {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 80%;
        height: 80%;
        background: radial-gradient(
          circle,
          rgba(217, 58, 53, 0.2) 0%,
          transparent 70%
        );
        filter: blur(40px);
        z-index: -1;
      }

      img {
        width: 100%;
        height: auto;
        border-radius: 24px;
        box-shadow: 0 30px 60px var(--shadow-large);
      }
    }
  }

  @media (max-width: 768px) {
    padding: 100px 20px 80px;
    min-height: 350px;

    .heroSectionContainer {
      .heroTitle {
        font-size: 42px;
      }

      .heroDescription {
        font-size: 16px;
      }

      .breadcrumb {
        flex-wrap: wrap;
      }
    }

    .floatingShape {
      display: none;
    }
  }
`;

export const BodySection = styled.section`
  width: 100%;
  background: var(--bg-primary);
  padding: 100px 40px 120px;
  overflow: hidden;

  .bodySectionContainer {
    position: relative;
    max-width: 1200px;
    width: 100%;
    margin: 0 auto;

    .contentGrid {
      display: grid;
      grid-template-columns: 1fr 380px;
      gap: 60px;
      align-items: start;
    }

    .mainContent {
      .sectionLabel {
        display: inline-flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 24px;

        .line {
          width: 30px;
          height: 3px;
          background: var(--accent);
          border-radius: 2px;
        }

        span {
          font-family: Poppins;
          font-size: 14px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 2px;
          color: var(--accent);
        }
      }

      h2 {
        font-family: Poppins;
        font-size: 42px;
        font-weight: 700;
        color: var(--text-primary);
        line-height: 1.2;
        margin: 0 0 32px;

        .highlight {
          background: linear-gradient(
            135deg,
            var(--accent) 0%,
            var(--primary) 100%
          );
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      }

      .leadParagraph {
        font-family: Poppins;
        font-size: 20px;
        line-height: 1.8;
        color: var(--text-secondary);
        margin: 0 0 32px;
        padding-left: 24px;
        border-left: 4px solid var(--accent);
      }

      .contentParagraph {
        font-family: Poppins;
        font-size: 17px;
        line-height: 1.9;
        color: var(--text-secondary);
        margin: 0 0 24px;
      }
    }

    .sidebar {
      position: sticky;
      top: 120px;
      width: 100%;
      max-width: 380px;

      .imageCard {
        position: relative;
        border-radius: 24px;
        overflow: hidden;
        margin-bottom: 32px;

        &::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            180deg,
            transparent 50%,
            rgba(0, 0, 0, 0.6) 100%
          );
          z-index: 1;
        }

        img {
          width: 100%;
          height: 300px;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        &:hover img {
          transform: scale(1.05);
        }
      }

      .ctaCard {
        background: linear-gradient(135deg, var(--accent) 0%, #c12f2a 100%);
        border-radius: 24px;
        padding: 40px 32px;
        text-align: center;

        h3 {
          font-family: Poppins;
          font-size: 24px;
          font-weight: 700;
          color: #fff;
          margin: 0 0 12px;
        }

        p {
          font-family: Poppins;
          font-size: 14px;
          color: rgba(255, 255, 255, 0.9);
          margin: 0 0 24px;
          line-height: 1.6;
        }

        .ctaButton {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 14px 32px;
          background: #fff;
          color: var(--accent);
          font-family: Poppins;
          font-size: 15px;
          font-weight: 600;
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.3s ease;

          &:hover {
            transform: translateY(-3px);
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
          }

          svg {
            width: 18px;
            height: 18px;
          }
        }
      }
    }

    .featuresGrid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 32px;
      margin-top: 80px;

      .featureCard {
        background: var(--card-bg);
        border-radius: 20px;
        padding: 36px 28px;
        border: 2px solid var(--card-border);
        transition: all 0.3s ease;

        &:hover {
          border-color: var(--accent);
          transform: translateY(-8px);
          box-shadow: 0 20px 50px var(--shadow-medium);
        }

        .featureIcon {
          width: 60px;
          height: 60px;
          border-radius: 16px;
          background: linear-gradient(
            135deg,
            rgba(217, 58, 53, 0.1) 0%,
            rgba(43, 59, 124, 0.1) 100%
          );
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 24px;

          svg {
            width: 28px;
            height: 28px;
            color: var(--accent);
          }
        }

        h4 {
          font-family: Poppins;
          font-size: 18px;
          font-weight: 700;
          color: var(--text-primary);
          margin: 0 0 12px;
        }

        p {
          font-family: Poppins;
          font-size: 14px;
          line-height: 1.7;
          color: var(--text-secondary);
          margin: 0;
        }
      }
    }
  }

  @media (max-width: 1024px) {
    padding: 80px 30px 100px;

    .bodySectionContainer {
      .contentGrid {
        grid-template-columns: 1fr;
        gap: 50px;
      }

      .sidebar {
        position: static;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 24px;
        max-width: 100%;

        .imageCard {
          margin-bottom: 0;
        }

        .ctaCard {
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
      }

      .featuresGrid {
        grid-template-columns: repeat(2, 1fr);
        margin-top: 60px;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 60px 20px 80px;

    .bodySectionContainer {
      .mainContent {
        h2 {
          font-size: 32px;
        }

        .leadParagraph {
          font-size: 17px;
        }
      }

      .sidebar {
        grid-template-columns: 1fr;
      }

      .featuresGrid {
        grid-template-columns: 1fr;
        gap: 20px;
      }
    }
  }
`;
